import React, { useState, useEffect } from "react";
import DraggableBottomSheet from "../../../components/common/draggable-bottom-sheet";

interface PickupTimeFilterBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (startTime: string, endTime: string) => void;
  onReset?: () => void;
  initialStartTime?: string;
  initialEndTime?: string;
  hasFilter?: boolean;
}

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const MIN_TIME_MINUTES = 12 * 60;
const MIN_RANGE_VALUE = Math.floor(MIN_TIME_MINUTES / 30);
const MIN_END_TIME_MINUTES = 12 * 60 + 30;
const MIN_END_RANGE_VALUE = Math.floor(MIN_END_TIME_MINUTES / 30);
const MAX_TIME_MINUTES = 23 * 60 + 55;
const MAX_RANGE_VALUE = Math.floor(MAX_TIME_MINUTES / 30);

const timeToRangeValue = (time: string): number => {
  const minutes = timeToMinutes(time);
  const value = Math.floor(minutes / 30);
  if (minutes < MIN_TIME_MINUTES) return MIN_RANGE_VALUE;
  if (minutes > MAX_TIME_MINUTES) return MAX_RANGE_VALUE;
  return value;
};

const rangeValueToTime = (value: number): string => {
  if (value >= MAX_RANGE_VALUE) return "23:55";
  const minutes = value * 30;
  return minutesToTime(minutes);
};

const PickupTimeFilterBottomSheet: React.FC<
  PickupTimeFilterBottomSheetProps
> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  initialStartTime = "12:00",
  initialEndTime = "12:30",
  hasFilter = false,
}) => {
  const [startValue, setStartValue] = useState(
    timeToRangeValue(initialStartTime)
  );
  const [endValue, setEndValue] = useState(timeToRangeValue(initialEndTime));
  const sliderRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setStartValue(timeToRangeValue(initialStartTime));
      setEndValue(timeToRangeValue(initialEndTime));
    }
  }, [isOpen, initialStartTime, initialEndTime]);

  const handleCancel = () => {
    setStartValue(timeToRangeValue(initialStartTime));
    setEndValue(timeToRangeValue(initialEndTime));
    onClose();
  };

  const handleApply = () => {
    onApply(rangeValueToTime(startValue), rangeValueToTime(endValue));
    onClose();
  };

  const handleReset = () => {
    if (onReset) onReset();
    onClose();
  };

  const startTimeDisplay = rangeValueToTime(startValue);
  const endTimeDisplay = rangeValueToTime(endValue);
  const minValue = MIN_RANGE_VALUE;
  const maxValue = MAX_RANGE_VALUE;
  const startPercent = ((startValue - minValue) / (maxValue - minValue)) * 100;
  const endPercent = ((endValue - minValue) / (maxValue - minValue)) * 100;

  const getValueFromClientX = (clientX: number) => {
    if (!sliderRef.current) return null;
    const rect = sliderRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const rawValue = MIN_RANGE_VALUE + ratio * (maxValue - minValue);
    return Math.round(rawValue);
  };

  const handleThumbMouseDown =
    (type: "start" | "end") =>
    (
      e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
    ) => {
      e.preventDefault();

      const handleMove = (event: { touches?: { [0]: { clientX: number } }; clientX?: number }) => {
        const clientX =
          event.touches && event.touches[0]
            ? event.touches[0].clientX
            : event.clientX;
        const value = getValueFromClientX(clientX ?? 0);
        if (value === null) return;

        if (type === "start") {
          const clampedValue = Math.max(
            MIN_RANGE_VALUE,
            Math.min(value, endValue - 1)
          );
          setStartValue(clampedValue);
        } else {
          const clampedValue = Math.max(
            Math.max(startValue + 1, MIN_END_RANGE_VALUE),
            Math.min(value, MAX_RANGE_VALUE)
          );
          setEndValue(clampedValue);
        }
      };

      const handleUp = () => {
        window.removeEventListener("mousemove", handleMove as () => void);
        window.removeEventListener("touchmove", handleMove as () => void);
        window.removeEventListener("mouseup", handleUp);
        window.removeEventListener("touchend", handleUp);
      };

      window.addEventListener("mousemove", handleMove as () => void);
      window.addEventListener("touchmove", handleMove as () => void);
      window.addEventListener("mouseup", handleUp);
      window.addEventListener("touchend", handleUp);
    };

  return (
    <DraggableBottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="pb-4">
        <h2 className="text-xl font-bold mb-1">👋 언제쯤 픽업 원하세요?</h2>
        <p className="text-sm text-gray-500 mb-6">드래그하여 조정해주세요</p>

        <div className="mb-8">
          <div className="flex justify-evenly items-center mb-4">
            <div>
              <span className="text-3xl font-bold text-[#1562fc]">
                {startTimeDisplay}
              </span>
            </div>
            <div className="text-center">
              <span className="text-2xl font-bold text-gray-800">~</span>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-[#1562fc]">
                {endTimeDisplay}
              </span>
            </div>
          </div>

          <div ref={sliderRef} className="relative h-10">
            <div className="absolute top-1/2 left-0 right-0 h-2 bg-gray-200 rounded-lg transform -translate-y-1/2" />
            <div
              className="absolute top-1/2 h-2 bg-[#1562fc] rounded-lg transform -translate-y-1/2 pointer-events-none"
              style={{
                left: `${startPercent}%`,
                width: `${endPercent - startPercent}%`,
              }}
            />
            <div
              className="absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center cursor-pointer"
              style={{ left: `${startPercent}%`, top: "50%" }}
              onMouseDown={handleThumbMouseDown("start")}
              onTouchStart={handleThumbMouseDown("start")}
            >
              <div className="w-4 h-4 bg-[#1562fc] rounded-full border-2 border-white shadow-md" />
            </div>
            <div
              className="absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center cursor-pointer"
              style={{ left: `${endPercent}%`, top: "50%" }}
              onMouseDown={handleThumbMouseDown("end")}
              onTouchStart={handleThumbMouseDown("end")}
            >
              <div className="w-4 h-4 bg-[#1562fc] rounded-full border-2 border-white shadow-md" />
            </div>
          </div>

          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>12:00</span>
            <span>18:00</span>
            <span>23:55</span>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {hasFilter && onReset && (
            <button
              onClick={handleReset}
              className="w-full bg-white text-gray-700 font-semibold py-2 px-4 rounded-xl transition-colors border border-gray-200"
            >
              초기화
            </button>
          )}
          <div className="flex justify-between gap-3">
            <button
              onClick={handleCancel}
              className="basis-3/10 bg-gray-100 text-gray-700 font-semibold py-4 px-4 rounded-xl hover:bg-gray-200 transition-colors"
              style={{ flexBasis: "30%" }}
            >
              취소
            </button>
            <button
              onClick={handleApply}
              className="basis-7/10 bg-[#1562fc] text-white font-bold py-4 px-4 rounded-xl hover:bg-[#1250d0] transition-colors"
              style={{ flexBasis: "70%" }}
            >
              적용
            </button>
          </div>
        </div>
      </div>
    </DraggableBottomSheet>
  );
};

export default PickupTimeFilterBottomSheet;
